import * as three from "three";
import ParticleMesh from "../../instancing/Meshes/ParticleMesh";
import RectangleMesh from "../../instancing/Meshes/RectangleMesh";
import getMaterialWithUniforms from "../../instancing/materials/getMaterialWithUniforms";
import { pathogenicityYScale } from "../../constants/pathogenicity";
import createCircleTexture from "../../instancing/textures/createCircleTexture";
import createDiskTexture from "../../instancing/textures/createDiskTexture";

const pointSize = 10;
const selectionSize = pointSize + 6;
const size = 16;
const diskText = createDiskTexture({ size });
const pxSize = (2 * size) / selectionSize;

const circleText = createCircleTexture({
  size: 32,
  fill: false,
  strokeWidth: pxSize,
});

export function getPetalsMesh() {
  const conf = {
    posX: 1,
    posY: 1,
    offset: 1,
    rgbaCode: 1,
  };

  const matconf = {
    alphaTest: 0.1,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  };

  const materialConf = getMaterialWithUniforms(matconf)
    .setOffset([0, 28])
    .setPaddingX([1, -1])
    .setPaddingY([0, 0, 0, 0])
    .setPointSize(pointSize)
    .setColorMap(Array(11).fill(new three.Vector4()))
    .setTexture(diskText.texture)
    .setTextureCoordMap(diskText.textureCoordsMap);

  return new ParticleMesh(materialConf, conf);
}

export function getLollipopBaseMesh() {
  const conf = {
    posX: 1,
    posY: 1,
    rgbaCode: 1,
  };
  const matConf = { disableRaycast: true };
  const materialConf = getMaterialWithUniforms(matConf)
    .setOffset([0, 0])
    .setPaddingX([-0.5, 0.5, -0.5, 0.5])
    .setPaddingY([0, 0, 0, 0])
    .setColorMap(Array(11).fill(new three.Vector4()));
  return new RectangleMesh(materialConf, conf);
}

export function getSelectionMesh() {
  const matconf = {
    disableRaycast: true,
    transparent: true,
  };

  const conf = {
    posX: 1,
    posY: 1,
    offset: 1,
    rgbaCode: 1,
  };
  const materialConf = getMaterialWithUniforms(matconf)
    .setOffset([0, 0])
    .setPaddingX([0, 0])
    .setPaddingY([0, 0, 0, 0])
    .setPointSize(selectionSize)
    .setColorMap([new three.Vector4(0.5, 0.5, 0.5, 0)])
    .setTexture(circleText.texture)
    .setTextureCoordMap(circleText.textureCoordsMap);

  return new ParticleMesh(materialConf, conf);
}

export function drawPetal(petals, mesh, colorBy, colorByKeys) {
  const count = petals.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  const setOffset = mesh.updateOffset({ count, isAppend });
  const colorImpact = colorBy === colorByKeys.colorByImpact;
  const colorKey = colorImpact ? "codingImpactColorIndex" : "pathoIndex";

  for (let i = 0; i < count; i++) {
    const petal = petals[i];
    const { pathogenicityDetails, index, lollipop } = petal;
    const detail = pathogenicityDetails[0];
    const { variant } = detail;
    const colorIndex = detail[colorKey];

    setX(i, variant.region.g0 + 0.5);
    setRgb(i, colorIndex);

    if (lollipop.petalSize) {
      const offset = getPetalOffset(index, lollipop.petalSize);
      setOffset(i, offset[0], offset[1]);
    }
  }
}

export function updateVisPetal(items, mesh, filterSet) {
  const setY = mesh.updateY({ count: items.length, isAppend: false });
  const { impactFilters, dbFilters } = filterSet;
  const dbArr = Array.from(dbFilters.values());
  if (impactFilters.size === 0 && dbFilters.size === 0) {
    for (let i = 0; i < items.length; i++) {
      const y = pathogenicityYScale[items[i]?.pathoIndex];
      setY(i, y);
    }
  } else {
    for (let i = 0; i < items.length; i++) {
      const { pathoIndex, codingImpactIndex: ci } = items[i];
      const hasImpact = impactFilters.size === 0 || impactFilters.has(ci);
      const hasDb = dbFilters.size === 0 || items[i].hasAnyDb(dbArr);
      const isVisible = hasImpact && hasDb;
      const y = isVisible ? pathogenicityYScale[pathoIndex] : -5000;
      setY(i, y);
    }
  }
}

export function updatePetalColor(items, mesh, colorBy, colorByKeys) {
  const setRgb = mesh.updateRgba({ count: items.length, isAppend: false });
  const colorImpact = colorBy === colorByKeys.colorByImpact;
  const colorKey = colorImpact ? "codingImpactColorIndex" : "pathoIndex";

  for (let i = 0; i < items.length; i++) {
    const rgbInd = items[i]?.[colorKey];
    setRgb(i, rgbInd);
  }
}

export function updateVisBase(items, mesh, filterSet) {
  const { impactFilters, dbFilters } = filterSet;
  const impactArr = Array.from(impactFilters.values());
  const dbArr = Array.from(dbFilters.values());
  const setY = mesh.updateY({ count: items.length, isAppend: false });

  for (let i = 0; i < items.length; i++) {
    const base = items[i];
    const { pathoIndex } = base;
    const isVisible = base.contains(impactArr, impactFilters, dbArr);

    if (isVisible) {
      const y = pathogenicityYScale[pathoIndex];
      if (y > 0.5) {
        setY(i, 0.5, 0.5, y, y);
      } else {
        setY(i, y, y, 0.5, 0.5);
      }
    } else {
      setY(i, -5000, -5000, -5000, -5000);
    }
  }
}

export function drawSelection(petals, mesh) {
  const count = petals.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setOffset = mesh.updateOffset({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const petal = petals[i];
    const { pathogenicityDetails, index, lollipop } = petal;
    const { variant } = pathogenicityDetails[0];

    setX(i, variant.region.g0 + 0.5);

    if (lollipop.petalSize) {
      const offset = getPetalOffset(index, lollipop.petalSize);
      setOffset(i, offset[0], offset[1]);
    }
  }
}

export function drawBase(lollipops, mesh) {
  const count = lollipops.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const lollipop = lollipops[i];
    const { region, pathoIndex } = lollipop;
    const x = region.g0 + 0.5;
    const rgb = pathoIndex;
    setX(i, x, x, x, x);
    setRgb(i, rgb, rgb, rgb, rgb);
  }
}

function getPetalOffsetRadius(total) {
  if (total === 1) {
    return 0;
  }
  if (total === 2) {
    return pointSize * 0.4;
  }
  if (total === 3) {
    return pointSize * 0.6;
  }
  if (total < 6) {
    return pointSize;
  }
  return pointSize * 1.2;
}

function getPetalOffset(index, total) {
  if (total === 1 || (total > 3 && index === 0)) {
    return [0, 0];
  }
  const radius = getPetalOffsetRadius(total) * 0.8;
  const shift = total > 3 ? 1 : 0;
  const indexPercent = (index - shift) / (total - shift);
  const radian = 2 * Math.PI * indexPercent;
  const x = Math.sin(radian) * radius;
  const y = Math.cos(radian) * radius;
  return [x, y];
}
